/**
 * VoiceWidget themes - предустановленные темы для разных брендов
 *
 * Использование:
 * import { themes } from './themes';
 * <VoiceWidget theme={themes.shiftgears} />
 * <VoiceWidget theme={themes.shiftgearsDark} />
 *
 * Кастомизация:
 * <VoiceWidget theme={{
 *   ...themes.shiftgears,
 *   spacing: { barHeight: '56px' },
 *   radius: { bar: '28px' }
 * }} />
 *
 * Accent режим:
 * <VoiceWidget theme={getTheme('shiftgears', 'light', { accent: true })} />
 * В режиме accent для voice и collapsed состояний:
 * - Фон виджета = primaryColor
 * - Текст и иконки = белый или чёрный (автоматически по контрастности)
 */

/**
 * Парсит HEX цвет в RGB компоненты
 * @param {string} hex - цвет в формате #RRGGBB или #RGB
 * @returns {{r: number, g: number, b: number}} RGB компоненты (0-255)
 */
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    return {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
    };
  }
  // Поддержка короткого формата #RGB
  const shortResult = /^#?([a-f\d])([a-f\d])([a-f\d])$/i.exec(hex);
  if (shortResult) {
    return {
      r: parseInt(shortResult[1] + shortResult[1], 16),
      g: parseInt(shortResult[2] + shortResult[2], 16),
      b: parseInt(shortResult[3] + shortResult[3], 16),
    };
  }
  return { r: 0, g: 0, b: 0 };
};

/**
 * Затемняет HEX цвет на заданный процент
 * @param {string} hex - цвет в формате #RRGGBB
 * @param {number} percent - процент затемнения (0-100)
 * @returns {string} затемнённый цвет в формате #RRGGBB
 */
const darkenColor = (hex, percent) => {
  const { r, g, b } = hexToRgb(hex);
  const factor = 1 - percent / 100;
  const newR = Math.max(0, Math.round(r * factor));
  const newG = Math.max(0, Math.round(g * factor));
  const newB = Math.max(0, Math.round(b * factor));
  return `#${newR.toString(16).padStart(2, '0')}${newG
    .toString(16)
    .padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};

/**
 * Вычисляет относительную яркость цвета по формуле WCAG 2.1
 * https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
 * @param {string} hex - цвет в формате #RRGGBB
 * @returns {number} относительная яркость (0-1)
 */
const getLuminance = (hex) => {
  const { r, g, b } = hexToRgb(hex);

  const toLinear = (c) => {
    const sRGB = c / 255;
    return sRGB <= 0.03928
      ? sRGB / 12.92
      : Math.pow((sRGB + 0.055) / 1.055, 2.4);
  };

  const R = toLinear(r);
  const G = toLinear(g);
  const B = toLinear(b);

  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
};

/**
 * Осветляет HEX цвет на заданный процент — зеркало `darkenColor`.
 * Тянем каждый канал к 255, а не умножаем: умножение почти чёрный цвет
 * (Autodis, #000000) не сдвинуло бы вообще.
 * @param {string} hex - цвет в формате #RRGGBB
 * @param {number} percent - процент осветления (0-100)
 * @returns {string} осветлённый цвет в формате #RRGGBB
 */
const lightenColor = (hex, percent) => {
  const { r, g, b } = hexToRgb(hex);
  const k = Math.min(100, Math.max(0, percent)) / 100;
  const up = (c) => Math.min(255, Math.round(c + (255 - c) * k));
  return `#${up(r).toString(16).padStart(2, '0')}${up(g)
    .toString(16)
    .padStart(2, '0')}${up(b).toString(16).padStart(2, '0')}`;
};

/**
 * Коэффициент контраста двух цветов по WCAG 2.1 (от 1:1 до 21:1).
 * https://www.w3.org/TR/WCAG21/#dfn-contrast-ratio
 * @param {string} a - цвет в формате #RRGGBB
 * @param {string} b - цвет в формате #RRGGBB
 * @returns {number} коэффициент контраста
 */
export const getContrastRatio = (a, b) => {
  const la = getLuminance(a);
  const lb = getLuminance(b);
  const light = Math.max(la, lb);
  const dark = Math.min(la, lb);
  return (light + 0.05) / (dark + 0.05);
};

/**
 * Минимальный контраст бренд-цвета к поверхности виджета.
 *
 * 3:1 — порог WCAG 2.1 для НЕтекстовых элементов (1.4.11): границы, иконки,
 * заливка кнопок, индикаторы. Именно так примари и работает в виджете. Для
 * текста поверх самого примари контраст считает `getContrastColor` — это
 * отдельная величина и её мы не трогаем.
 */
const PRIMARY_MIN_CONTRAST = 3;

/**
 * Подбирает оттенок бренд-цвета, читаемый на заданной поверхности.
 *
 * Тёмная тема раньше брала примари как есть: `--widget-bg` становился
 * `#1a1a1a`, а бренд оставался прежним. У тёмных брендов это провал — у
 * Autodis (`#000000`) контраст к тёмному фону 1.0:1, то есть кнопка сливалась
 * с фоном полностью.
 *
 * Осветляем шагами и останавливаемся, как только порог взят: цвет должен
 * уехать от бренда ровно настолько, насколько нужно для читаемости, и ни на
 * шаг дальше. Если контраст и так в порядке — возвращаем исходный цвет
 * нетронутым, поэтому у большинства дилеров ничего не поменяется.
 *
 * @param {string} color - бренд-цвет в формате #RRGGBB
 * @param {string} surface - цвет поверхности в формате #RRGGBB
 * @returns {string} исходный или осветлённый цвет
 */
export const resolveReadablePrimary = (color, surface) => {
  if (!color || !surface) return color;
  if (getContrastRatio(color, surface) >= PRIMARY_MIN_CONTRAST) return color;
  for (let step = 10; step <= 90; step += 10) {
    const candidate = lightenColor(color, step);
    if (getContrastRatio(candidate, surface) >= PRIMARY_MIN_CONTRAST) return candidate;
  }
  // Недостижимо для реальных поверхностей, но пусть функция всегда возвращает
  // цвет, а не undefined: белый гарантированно контрастен тёмному фону.
  return '#ffffff';
};

/**
 * Определяет контрастный цвет (белый или чёрный) для заданного фона
 * Использует YIQ формулу яркости, которая лучше соответствует
 * человеческому восприятию для ярких насыщенных цветов
 * @param {string} backgroundColor - цвет фона в формате #RRGGBB
 * @returns {string} '#ffffff' для тёмных фонов, '#000000' для светлых
 */
export const getContrastColor = (backgroundColor) => {
  const { r, g, b } = hexToRgb(backgroundColor);

  // YIQ формула учитывает человеческое восприятие яркости
  // Коэффициенты: R=299, G=587, B=114 (сумма = 1000)
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;

  // Порог 128 (середина диапазона 0-255)
  // Для ярких цветов (оранжевый, красный) это даёт белый текст
  return yiq >= 128 ? '#000000' : '#ffffff';
};

/**
 * Цвета для светлой темы (по умолчанию)
 */
const lightColors = {
  background: '#ffffff',
  backgroundSecondary: '#fafafa',
  backgroundTertiary: '#f5f5f5',
  backgroundHover: '#ebebeb',
  text: '#1a1a1a',
  textSecondary: '#666666',
  textMuted: '#999999',
  border: '#f0f0f0',
  shadow: 'rgba(0, 0, 0, 0.08)',
  shadowStrong: 'rgba(0, 0, 0, 0.12)',
  shadowPanel: '0 8px 32px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)',
  // Message bubbles
  userBubble: 'var(--widget-primary)',
  userBubbleText: '#ffffff',
  assistantBubble: '#f5f5f5',
  assistantBubbleText: '#1a1a1a',
  // Status
  statusActive: '#22c55e',
  statusInactive: '#cccccc',
  // Error colors
  errorBg: '#fef2f2',
  errorBorder: '#fecaca',
  errorText: '#991b1b',
  // Focus
  focusShadow: 'rgba(199, 48, 48, 0.15)',
};

/**
 * Цвета для тёмной темы
 */
const darkColors = {
  background: '#1a1a1a',
  backgroundSecondary: '#242424',
  backgroundTertiary: '#2a2a2a',
  backgroundHover: '#333333',
  text: '#ffffff',
  textSecondary: '#b0b0b0',
  textMuted: '#808080',
  border: '#333333',
  shadow: 'rgba(0, 0, 0, 0.3)',
  shadowStrong: 'rgba(0, 0, 0, 0.4)',
  shadowPanel: '0 8px 32px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.3)',
  // Message bubbles
  userBubble: 'var(--widget-primary)',
  userBubbleText: '#ffffff',
  assistantBubble: '#2a2a2a',
  assistantBubbleText: '#ffffff',
  // Status
  statusActive: '#22c55e',
  statusInactive: '#666666',
  // Error colors (dark theme)
  errorBg: '#450a0a',
  errorBorder: '#7f1d1d',
  errorText: '#fecaca',
  // Focus
  focusShadow: 'rgba(199, 48, 48, 0.25)',
};

/**
 * Дефолтные размеры (можно переопределить в теме)
 */
const defaultSizing = {
  // Button sizes
  btnSm: '40px',
  btnMd: '40px', // Minimum tap target size
  btnLg: '40px',
  btnXs: '28px',
  // Bar heights
  barHeight: '56px',
  barHeightMobile: '44px',
  // Input
  inputHeight: '40px',
  inputHeightMobile: '40px', // Minimum tap target size
  // Icons
  iconSm: '18px',
  iconMd: '20px',
  iconLg: '32px',
  iconXs: '16px',
  // Scale transforms
  scaleHover: '1.05',
  scaleActive: '0.98',
};

/**
 * Дефолтные отступы (4px grid)
 */
const defaultSpacing = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  '2xl': '24px',
};

/**
 * Дефолтные скругления
 */
const defaultRadius = {
  sm: '4px',
  md: '8px',
  lg: '16px',
  xl: '20px',
  '2xl': '24px',
  full: '9999px',
  // Specific components
  bar: '28px',
  panel: '32px',
  button: '50%',
  input: '20px',
  bubble: '16px',
  bubbleTail: '4px',
};

/**
 * Стили виджета - определяют акцентный цвет
 */
/**
 * Шрифты тенантов (#1330).
 *
 * Шрифт — свойство БРЕНДА, а не приложения: у каждого тенанта свой, ровно как
 * свой primaryColor. До этого Roboto был зашит в двух местах — дефолтом в
 * `--theme-font-family` и константой в `bootstrap.jsx`, — и доставался всем
 * подряд, включая тех, у кого фирменный шрифт другой.
 *
 * `stack` уходит в CSS, `googleFamily` — в запрос к Google Fonts. Держим их
 * рядом, потому что разъехавшись они дают самый неприятный отказ: шрифт
 * объявлен, но не загружен, и подмена видна только на чужой машине.
 */
export const THEME_FONTS = {
  'source-sans': {
    stack:
      "'Source Sans 3', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    googleFamily: 'Source+Sans+3:wght@300;400;600;700',
  },
  roboto: {
    stack:
      "'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
    googleFamily: 'Roboto:wght@300;400;500;700;900',
  },
  raleway: {
    stack: "'Raleway', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    googleFamily: 'Raleway:wght@400;500;600;700',
  },
};

const styles = {
  /**
   * Default — тот же брендовый синий, что и `shiftgears` ниже: это
   * `--sg-blue-9`. Значения совпадают намеренно, дефолт «без стиля» и есть
   * ShiftGears. Для demo.shiftgears.ai используем именно `shiftgears` —
   * имя говорит, что это выбор, а не отсутствие выбора.
   */
  default: {
    primaryColor: '#2142FF',
    name: 'Default (B2B UI)',
  },

  /**
   * ShiftGears — актуальный бренд. Синий = `--sg-blue-9` из
   * `libs/design-tokens/colors.css`, тот же, что на сайте и в панели.
   * Это стиль demo.shiftgears.ai (задаётся через VITE_WIDGET_STYLE в
   * runtimeConfig деплоя — на клиентские страницы он не попадает,
   * там widget.jsx кладёт свой window.runtimeConfig).
   */
  shiftgears: {
    primaryColor: '#2142FF',
    name: 'ShiftGears',
  },

  /**
   * @deprecated Оранжевый бренд ShiftGears прошлого поколения (#F25603).
   *
   * ⛔ НЕ перекрашивать и не удалять без инвентаризации тенантов.
   * `config/runtimeConfig.js, геттер WIDGET_STYLE` отдаёт именно этот стиль как дефолт для
   * ЛЮБОГО эмбеда, у которого в widget-config не заполнен `appearance.style`.
   * То есть значение ниже — это фактический цвет виджета у неизвестного числа
   * живых клиентов, а не «цвет демо». Ретайр — в #1324, после запроса к
   * b2b-api «у кого пустой appearance.style».
   *
   * Новый код должен брать `shiftgears`.
   */
  'demo-shift-gears': {
    primaryColor: '#F25603',
    name: 'Demo Shift Gears (legacy orange)',
  },

  /**
   * Andy's Motors - красный (#c73030)
   */
  andys: {
    // Andys brand red — взято с живого сайта andys-motors.com (#da3227, #1108).
    // Раньше был приглушённый #c73030, потом #E31837 из stub — но настоящий
    // сайтовый red = #da3227. В проде токен всё равно отдаёт свои colors поверх.
    primaryColor: '#da3227',
    name: "Andy's Motors",
    // Roboto — их шрифт. Раньше он был дефолтом ВСЕХ тенантов (константа в
    // bootstrap.jsx + дефолт токена); теперь принадлежит тому, чей он есть.
    font: 'roboto',
  },

  /**
   * Autodis (Luxembourg) — диллер OMODA + JAECOO.
   * Бренд-палитра с omodajaecoo.be: чёрный primary, антрацит accent,
   * светло-серый фон. Issue #884 — showroom flow для new-model каталога.
   *
   * Note: VoiceWidget consumes only `primaryColor` + `name` here.
   * Полный пресет (radii + widget surface + paddings) для лендинга
   * и каталога живёт в `dealerPresets.js` — это canonical источник,
   * который `pushThemeToDocumentElement` мапит в `--theme-*` /
   * `--widget-*` через `THEME_MAPPING`.
   */
  autodis: {
    primaryColor: '#000000',
    name: 'Autodis (OMODA/JAECOO)',
    // Raleway — брендовый шрифт OMODA/JAECOO (#884). До этого он был вшит
    // литералом в NewModelLanding.css и грузился на КАЖДОЙ странице витрины,
    // хотя нужен только здесь.
    font: 'raleway',
  },
};

/** Шрифт по умолчанию — наш. Тенант переопределяет полем `font` в пресете. */
export const DEFAULT_FONT_KEY = 'source-sans';

/** Ключ шрифта для стиля: `andys` → roboto, `autodis` → raleway, иначе наш. */
export const getThemeFontKey = (styleName) =>
  styles[styleName]?.font || DEFAULT_FONT_KEY;

/** Google-спецификация семейства для стиля — её грузит bootstrap. */
export const getThemeFontFamilySpec = (styleName) =>
  THEME_FONTS[getThemeFontKey(styleName)].googleFamily;

/**
 * Создаёт тему по style и mode
 * @param {string} style - Стиль виджета: 'default' | 'shiftgears' | 'andys' | 'autodis'
 *                          ('demo-shift-gears' — legacy, см. комментарий у пресета)
 * @param {string} mode - Режим: 'light' | 'dark'
 * @param {Object} options - Дополнительные опции
 * @param {string|boolean} options.accent - Accent режим: 'off'|false, 'bg'|true, 'buttons'
 * @param {boolean} options.glass - Включить glass effect (glassmorphism)
 * @returns {Object} Объект темы
 */
export const getTheme = (style = 'default', mode = 'light', options = {}) => {
  const { accent = 'off', glass = false } = options;
  const styleConfig = styles[style] || styles.default;
  const colors = mode === 'dark' ? darkColors : lightColors;
  // Шрифт тенанта наравне с цветом: у каждого бренда свой (#1330).
  const fontStack = THEME_FONTS[styleConfig.font || DEFAULT_FONT_KEY].stack;
  const accentColor = mode === 'dark' ? '#ffffff' : '#1a1a1a';

  // Бренд-цвет под тему. Раньше он был один на обе: `--widget-bg` в тёмной
  // становился `#1a1a1a`, а примари оставался прежним — у тёмных брендов
  // кнопка сливалась с фоном (Autodis `#000000` даёт контраст 1.0:1).
  // Осветляем ТОЛЬКО когда порог 3:1 не взят, поэтому у большинства дилеров
  // цвет остаётся прежним байт в байт.
  const primaryColor = resolveReadablePrimary(
    styleConfig.primaryColor,
    colors.background
  );

  // Нормализация accent: boolean → string для обратной совместимости
  const normalizedAccent =
    accent === true ? 'bg' : accent === false ? 'off' : accent;

  // Контраст текста НА примари считаем от того оттенка, который реально
  // покрасит кнопку, — иначе на осветлённом фоне остался бы белый текст.
  const accentContrastColor = getContrastColor(primaryColor);

  return {
    primaryColor,
    accentColor,
    accent: normalizedAccent, // 'off' | 'bg' | 'buttons'
    glass, // Флаг glass effect (glassmorphism)
    accentContrastColor, // Контрастный цвет для текста/иконок в accent режиме
    name: `${styleConfig.name} (${mode === 'dark' ? 'Dark' : 'Light'}${
      normalizedAccent !== 'off' ? `, Accent ${normalizedAccent}` : ''
    }${glass ? ', Glass' : ''})`,
    variant: mode,
    // Шрифт бренда — читает `getThemeStyles` и кладёт inline (#1330).
    fontFamily: fontStack,
    colors,
    sizing: defaultSizing,
    spacing: defaultSpacing,
    radius: defaultRadius,
  };
};

/**
 * Предустановленные темы для обратной совместимости
 */
export const themes = {
  // Default themes
  default: getTheme('default', 'light'),
  defaultDark: getTheme('default', 'dark'),
  defaultAccent: getTheme('default', 'light', { accent: true }),
  defaultDarkAccent: getTheme('default', 'dark', { accent: true }),

  // ShiftGears themes. До #1321 эти четыре ключа указывали на легаси-оранжевый
  // `demo-shift-gears` — то есть пресет с именем бренда отдавал не цвет бренда.
  // Клиентские поверхности не задеты: AppWidget/AppWidgetFlexible/AppWidgetChatOnly
  // и registry/Chat всегда передают `theme` явно из config.WIDGET_STYLE.
  shiftgears: getTheme('shiftgears', 'light'),
  shiftgearsDark: getTheme('shiftgears', 'dark'),
  shiftgearsAccent: getTheme('shiftgears', 'light', { accent: true }),
  shiftgearsDarkAccent: getTheme('shiftgears', 'dark', { accent: true }),

  // Andy's Motors themes
  andys: getTheme('andys', 'light'),
  andysDark: getTheme('andys', 'dark'),
  andysAccent: getTheme('andys', 'light', { accent: true }),
  andysDarkAccent: getTheme('andys', 'dark', { accent: true }),

  // Autodis (OMODA/JAECOO) themes — чёрно-белый минимализм
  autodis: getTheme('autodis', 'light'),
  autodisDark: getTheme('autodis', 'dark'),
  autodisAccent: getTheme('autodis', 'light', { accent: true }),
  autodisDarkAccent: getTheme('autodis', 'dark', { accent: true }),
};

/**
 * Хелпер для получения CSS переменных из темы
 * @param {Object} theme - объект темы
 * @param {Object} options - опции генерации стилей
 * @param {boolean} options.isAccentMode - применить accent стили (для voice/collapsed режимов)
 * @returns {Object} - объект со стилями для CSS variables
 */
export const getThemeStyles = (theme, options = {}) => {
  const { isAccentMode = false } = options;
  const colors = theme.colors || lightColors;
  const sizing = { ...defaultSizing, ...theme.sizing };
  const spacing = { ...defaultSpacing, ...theme.spacing };
  const radius = { ...defaultRadius, ...theme.radius };

  // Базовые стили
  const primaryColor = theme.primaryColor || '#2142FF';
  const primaryColorHover = darkenColor(primaryColor, 10); // 10% darker for hover
  // Шрифт кладём inline вместе с цветом — тем же механизмом, что и
  // `--widget-primary`. Иначе дефолт из CSS доставался бы всем тенантам
  // одинаковым, а он теперь НАШ (Source Sans 3), не их.
  const fontFamily = theme.fontFamily || THEME_FONTS[DEFAULT_FONT_KEY].stack;
  const baseStyles = {
    '--widget-font-family': fontFamily,
    '--theme-font-family': fontFamily,
    // Primary colors (both --widget-* and --theme-* for compatibility)
    '--widget-primary': primaryColor,
    '--widget-primary-hover': primaryColorHover,
    '--theme-primary': primaryColor,
    '--theme-primary-hover': primaryColorHover,
    '--widget-accent': theme.accentColor || '#1a1a1a',

    // Background colors
    '--widget-bg': colors.background,
    '--widget-bg-secondary': colors.backgroundSecondary,
    '--widget-bg-tertiary': colors.backgroundTertiary,
    '--widget-bg-hover': colors.backgroundHover,

    // Text colors
    '--widget-text': colors.text,
    '--widget-text-secondary': colors.textSecondary,
    '--widget-text-muted': colors.textMuted,

    // Border & shadow
    '--widget-border': colors.border,
    '--widget-shadow': colors.shadow,
    '--widget-shadow-strong': colors.shadowStrong,
    '--widget-shadow-panel': colors.shadowPanel,
    // Шкала, собранная из цвета темы. В widget-tokens.css она объявлена на
    // :root через var(--widget-shadow), а цвет тема ставит сюда, на
    // контейнер, — с :root тёмная тема дилера на тени не влияла (#1533).
    '--widget-shadow-sm': `0 1px 3px ${colors.shadow}`,
    '--widget-shadow-md': `0 2px 8px ${colors.shadow}`,
    '--widget-shadow-lg': `0 4px 20px ${colors.shadow}`,
    '--widget-shadow-xl': `0 4px 24px ${colors.shadowStrong}`,
    '--widget-shadow-bar': `0 4px 20px ${colors.shadow}, 0 2px 8px ${colors.shadow}`,
    // Роли (#1533) — по той же причине объявлены и здесь.
    '--widget-shadow-control': 'var(--widget-shadow-sm)',
    '--widget-shadow-control-glass': 'var(--widget-glass-shadow)',
    '--widget-shadow-control-glass-hover': 'var(--widget-glass-shadow-hover)',
    '--widget-shadow-raised': 'var(--widget-shadow-bar)',
    '--widget-shadow-raised-glass': 'var(--widget-glass-shadow-bar)',
    '--widget-shadow-overlay': 'var(--widget-shadow-panel)',
    '--widget-shadow-modal': 'var(--widget-shadow-mobile-sheet)',

    // Message bubbles
    '--widget-user-bubble': colors.userBubble,
    '--widget-user-bubble-text': colors.userBubbleText,
    '--widget-assistant-bubble': colors.assistantBubble,
    '--widget-assistant-bubble-text': colors.assistantBubbleText,

    // Status
    '--widget-status-active': colors.statusActive,
    '--widget-status-inactive': colors.statusInactive,

    // Error colors
    '--widget-error-bg': colors.errorBg,
    '--widget-error-border': colors.errorBorder,
    '--widget-error-text': colors.errorText,

    // Focus
    '--widget-focus-shadow': colors.focusShadow,

    // Sizing
    '--widget-btn-sm': sizing.btnSm,
    '--widget-btn-md': sizing.btnMd,
    '--widget-btn-lg': sizing.btnLg,
    '--widget-bar-height': sizing.barHeight,
    '--widget-bar-height-mobile': sizing.barHeightMobile,
    '--widget-input-height': sizing.inputHeight,
    '--widget-input-height-mobile': sizing.inputHeightMobile,
    '--widget-icon-xs': sizing.iconXs,
    '--widget-icon-sm': sizing.iconSm,
    '--widget-icon-md': sizing.iconMd,
    '--widget-icon-lg': sizing.iconLg,
    '--widget-btn-xs': sizing.btnXs,

    // Scale transforms
    '--widget-scale-hover': sizing.scaleHover,
    '--widget-scale-active': sizing.scaleActive,

    // Spacing
    '--widget-space-xs': spacing.xs,
    '--widget-space-sm': spacing.sm,
    '--widget-space-md': spacing.md,
    '--widget-space-lg': spacing.lg,
    '--widget-space-xl': spacing.xl,
    '--widget-space-2xl': spacing['2xl'],

    // Radius
    '--widget-radius-sm': radius.sm,
    '--widget-radius-md': radius.md,
    '--widget-radius-lg': radius.lg,
    '--widget-radius-xl': radius.xl,
    '--widget-radius-2xl': radius['2xl'],
    '--widget-radius-full': radius.full,
    '--widget-radius-bar': radius.bar,
    '--widget-radius-panel': radius.panel,
    '--widget-radius-button': radius.button,
    '--widget-radius-input': radius.input,
    '--widget-radius-bubble': radius.bubble,
    '--widget-radius-bubble-tail': radius.bubbleTail,

    // Accent mode variables (always present, applied via CSS class)
    '--widget-accent-bg': theme.primaryColor || '#2142FF',
    '--widget-accent-contrast':
      theme.accentContrastColor ||
      getContrastColor(theme.primaryColor || '#2142FF'),

    // Glass effect variables (depend on theme variant)
    // Light mode: more opaque for deep glass effect
    // Dark mode: semi-transparent dark for glass effect
    '--widget-glass-bg':
      theme.variant === 'dark'
        ? 'rgba(30, 30, 30, 0.88)'
        : 'rgba(255, 255, 255, 0.88)',
    '--widget-glass-border':
      theme.variant === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(255, 255, 255, 0.5)',
    '--widget-glass-blur': '24px',
    '--widget-glass-btn-bg':
      theme.variant === 'dark'
        ? 'rgba(255, 255, 255, 0.2)'
        : 'rgba(255, 255, 255, 0.55)',
    '--widget-glass-btn-hover':
      theme.variant === 'dark'
        ? 'rgba(255, 255, 255, 0.35)'
        : 'rgba(255, 255, 255, 0.7)',
    // Glass input colors (theme-aware)
    '--widget-glass-input-bg':
      theme.variant === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.06)',
    '--widget-glass-input-bg-focus':
      theme.variant === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.08)',
    // Primary color with alpha for focus rings
    '--widget-primary-alpha': (() => {
      const hex = theme.primaryColor || '#2142FF';
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, 0.3)`;
    })(),
  };

  // Если accent bg режим активен, переопределяем стили для voice/collapsed
  if (theme.accent === 'bg' && isAccentMode) {
    const contrastColor =
      theme.accentContrastColor ||
      getContrastColor(theme.primaryColor || '#2142FF');
    // Полупрозрачная версия контрастного цвета для hover/tertiary
    const contrastColorAlpha =
      contrastColor === '#ffffff'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.1)';
    const contrastColorAlphaHover =
      contrastColor === '#ffffff'
        ? 'rgba(255, 255, 255, 0.25)'
        : 'rgba(0, 0, 0, 0.15)';

    // Конвертируем primaryColor в rgba для glass effect
    const primaryHex = theme.primaryColor || '#2142FF';
    const r = parseInt(primaryHex.slice(1, 3), 16);
    const g = parseInt(primaryHex.slice(3, 5), 16);
    const b = parseInt(primaryHex.slice(5, 7), 16);
    const glassAccentBg = `rgba(${r}, ${g}, ${b}, 0.88)`;
    const glassAccentBorder =
      contrastColor === '#ffffff'
        ? 'rgba(255, 255, 255, 0.5)'
        : 'rgba(0, 0, 0, 0.2)';
    // Enhanced button opacity for better visibility
    const glassAccentBtnBg =
      contrastColor === '#ffffff'
        ? 'rgba(255, 255, 255, 0.2)'
        : 'rgba(0, 0, 0, 0.15)';
    const glassAccentBtnHover =
      contrastColor === '#ffffff'
        ? 'rgba(255, 255, 255, 0.35)'
        : 'rgba(0, 0, 0, 0.25)';

    return {
      ...baseStyles,
      // Override for accent mode
      '--widget-bg': theme.primaryColor,
      '--widget-bg-secondary': theme.primaryColor,
      '--widget-bg-tertiary': contrastColorAlpha,
      '--widget-bg-hover': contrastColorAlphaHover,
      '--widget-text': contrastColor,
      '--widget-text-secondary': contrastColor,
      '--widget-text-muted': contrastColor,
      // Glass effect overrides for accent mode
      '--widget-glass-bg': glassAccentBg,
      '--widget-glass-border': glassAccentBorder,
      '--widget-glass-btn-bg': glassAccentBtnBg,
      '--widget-glass-btn-hover': glassAccentBtnHover,
    };
  }

  return baseStyles;
};

/**
 * CSS переменные которые использует VoiceWidget:
 *
 * COLORS:
 * --widget-primary: основной цвет (кнопки, акценты)
 * --widget-accent: цвет текста и контраста
 * --widget-bg: основной фон
 * --widget-bg-secondary: вторичный фон (header)
 * --widget-bg-tertiary: третичный фон (кнопки)
 * --widget-bg-hover: фон при hover
 * --widget-text: основной текст
 * --widget-text-secondary: вторичный текст
 * --widget-text-muted: приглушённый текст
 * --widget-border: цвет границ
 * --widget-shadow: тень
 * --widget-shadow-strong: усиленная тень
 * --widget-user-bubble: фон сообщения пользователя
 * --widget-user-bubble-text: текст сообщения пользователя
 * --widget-assistant-bubble: фон сообщения ассистента
 * --widget-assistant-bubble-text: текст сообщения ассистента
 *
 * SIZING:
 * --widget-btn-sm/md/lg: размеры кнопок
 * --widget-bar-height: высота collapsed/voice bar
 * --widget-input-height: высота инпута
 * --widget-icon-sm/md/lg: размеры иконок
 *
 * SPACING (4px grid):
 * --widget-space-xs: 4px
 * --widget-space-sm: 8px
 * --widget-space-md: 12px
 * --widget-space-lg: 16px
 * --widget-space-xl: 20px
 * --widget-space-2xl: 24px
 *
 * RADIUS:
 * --widget-radius-sm/md/lg/xl/2xl/full: общие скругления
 * --widget-radius-bar: скругление bar (collapsed/voice)
 * --widget-radius-panel: скругление chat panel
 * --widget-radius-input: скругление инпута
 * --widget-radius-bubble: скругление сообщений
 *
 * ACCENT MODE:
 * --widget-accent-bg: фон для accent режима (= primaryColor)
 * --widget-accent-contrast: контрастный цвет для текста/иконок (белый или чёрный)
 *
 * В accent режиме для voice и collapsed состояний:
 * - Фон виджета = primaryColor
 * - Текст и иконки = автоматически белый или чёрный по контрастности
 * - Chat режим не затрагивается
 *
 * USAGE:
 * // Использование предустановленной темы
 * import { themes } from './themes';
 * <VoiceWidget theme={themes.shiftgears} />
 * <VoiceWidget theme={themes.shiftgearsDark} />
 *
 * // Accent режим (оранжевый фон для voice/collapsed)
 * <VoiceWidget theme={themes.shiftgearsAccent} />
 *
 * // Создание темы по style и mode
 * import { getTheme } from './themes';
 * <VoiceWidget theme={getTheme('demo-shift-gears', 'dark')} />
 * <VoiceWidget theme={getTheme('andys', 'light')} />
 *
 * // Создание темы с accent режимом
 * <VoiceWidget theme={getTheme('demo-shift-gears', 'light', { accent: true })} />
 *
 * // Программное определение контрастного цвета
 * import { getContrastColor } from './themes';
 * const textColor = getContrastColor('#F25603'); // '#ffffff' (белый для оранжевого)
 * const textColor2 = getContrastColor('#FFFF00'); // '#000000' (чёрный для жёлтого)
 *
 * // Доступные styles: 'default', 'demo-shift-gears', 'andys'
 * // Доступные modes: 'light', 'dark'
 * // Доступные options: { accent: boolean }
 */

export default themes;
